import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { formatDateForApi } from '@app/core/utils/date.utils';

// Interface matching the Component's Form State
export interface ItinerariosFormState {
  page: number;
  limit: number;
  pol?: string;
  pod?: string;
  carrier?: string;
  vessel?: string;
  typeOfService?: number; // 1 = Liner, 2 = Charter
  startDate?: string | Date;
  endDate?: string | Date;
  dateEtaFrom?: string | Date;
  dateEtaTo?: string | Date;
}

export interface ItinerariosState {
  filters: ItinerariosFormState;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root'
})
export class ItinerariosStateService {
  private readonly STATE_KEY = 'itinerarios_state';
  private readonly STATE_EXPIRY_HOURS = 24;

  private readonly defaultState: ItinerariosState = {
    filters: {
      page: 1,
      limit: 10,
      pol: '',
      pod: '',
      carrier: '',
      vessel: '',
      typeOfService: undefined,
      startDate: undefined,
      endDate: undefined,
      dateEtaFrom: undefined,
      dateEtaTo: undefined
    },
    lastUpdated: Date.now()
  };

  private stateSubject = new BehaviorSubject<ItinerariosState>(this.defaultState);

  constructor() {
    this.initializeState();
  }

  private initializeState(): void {
    const savedState = this.loadStateFromStorage();
    if (savedState && !this.isStateExpired(savedState.lastUpdated)) {
      this.stateSubject.next(savedState);
    }
  }

  private isStateExpired(lastUpdated: number): boolean {
    const now = Date.now();
    const expiryTime = this.STATE_EXPIRY_HOURS * 60 * 60 * 1000;
    return (now - lastUpdated) > expiryTime;
  }

  getState(): Observable<ItinerariosState> {
    return this.stateSubject.asObservable();
  }

  getCurrentState(): ItinerariosState {
    return this.stateSubject.value;
  }

  updateState(partialState: Partial<ItinerariosState>): void {
    const currentState = this.stateSubject.value;
    const newState: ItinerariosState = {
      ...currentState,
      ...partialState,
      lastUpdated: Date.now()
    };
    this.stateSubject.next(newState);
    this.saveStateToStorage(newState);
  }

  updateFilters(filters: Partial<ItinerariosFormState>): void {
    const currentState = this.getCurrentState();
    const isPaginationChange = 'page' in filters || 'limit' in filters;

    this.updateState({
      filters: {
        ...currentState.filters,
        ...filters,
        // Reset to page 1 if any filter other than pagination changes, unless explicitly provided
        page: isPaginationChange ? (filters.page || currentState.filters.page) : 1
      }
    });
  }

  clearState(): void {
    this.stateSubject.next(this.defaultState);
    this.clearStateFromStorage();
  }

  private saveStateToStorage(state: ItinerariosState): void {
    try {
      localStorage.setItem(this.STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Error saving Itinerarios state to localStorage:', error);
    }
  }

  private loadStateFromStorage(): ItinerariosState | null {
    try {
      const saved = localStorage.getItem(this.STATE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Error loading Itinerarios state from localStorage:', error);
    }
    return null;
  }

  private clearStateFromStorage(): void {
    try {
      localStorage.removeItem(this.STATE_KEY);
    } catch (error) {
      console.warn('Error clearing Itinerarios state from localStorage:', error);
    }
  }

  /**
   * Convert state to query parameters for URL
   */
  stateToQueryParams(state: ItinerariosState): any {
    const params: any = {};
    const filters = state.filters;

    if (filters.pol) params.pol = filters.pol;
    if (filters.pod) params.pod = filters.pod;
    if (filters.carrier) params.carrier = filters.carrier;
    if (filters.vessel) params.vessel = filters.vessel;
    if (filters.typeOfService) params.typeOfService = filters.typeOfService.toString();

    if (filters.startDate) params.startDate = filters.startDate instanceof Date ? formatDateForApi(filters.startDate) : filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate instanceof Date ? formatDateForApi(filters.endDate) : filters.endDate;

    if (filters.dateEtaFrom) params.dateEtaFrom = filters.dateEtaFrom instanceof Date ? formatDateForApi(filters.dateEtaFrom) : filters.dateEtaFrom;
    if (filters.dateEtaTo) params.dateEtaTo = filters.dateEtaTo instanceof Date ? formatDateForApi(filters.dateEtaTo) : filters.dateEtaTo;

    // Pagination - Always include to ensure state is fully preserved
    params.page = (filters.page || 1).toString();
    params.limit = (filters.limit || 10).toString();

    return params;
  }

  /**
   * Convert query parameters from URL to state
   */
  queryParamsToState(params: any): ItinerariosState {
    return {
      filters: {
        pol: params.pol || '',
        pod: params.pod || '',
        carrier: params.carrier || '',
        vessel: params.vessel || '',
        typeOfService: params.typeOfService ? +params.typeOfService : undefined,
        startDate: params.startDate || undefined,
        endDate: params.endDate || undefined,
        dateEtaFrom: params.dateEtaFrom || undefined,
        dateEtaTo: params.dateEtaTo || undefined,
        page: params.page ? +params.page : 1,
        limit: params.limit ? +params.limit : 10
      },
      lastUpdated: Date.now()
    };
  }
}
